import { useEffect, useRef } from "react";
import { animate, motion, useInView, useMotionValue, useReducedMotion, useTransform } from "framer-motion";

const SWEEP = 2.5 * Math.PI;
const FINAL = [Math.PI, 0];
const RADIUS = 52;
const EASE = [0.45, 0.05, 0.3, 1];

// p = entrance progress, r = hover replay progress (both end at 1)
const orbit = (final, p, r) => {
  const pulse = Math.sin(Math.PI * r);
  const angle = final - SWEEP * (1 - p) - 2 * Math.PI * (1 - r);
  return {
    angle,
    radius: (0.15 + 0.85 * p) * (1 - 0.85 * pulse),
    depth: Math.max(1 - p, pulse),
    back: (1 - Math.sin(angle)) / 2,
  };
};

const OrbitImage = ({ img, index, progress, replay, still }) => {
  const final = FINAL[index];
  const at = (fn) => ([p, r]) => fn(still ? orbit(final, 1, 1) : orbit(final, p, r));
  const src = [progress, replay];
  const main = useTransform(src, at((o) => `${RADIUS * (o.radius * Math.cos(o.angle) - Math.cos(final))}%`));
  const cross = useTransform(src, at((o) => `${6 * Math.sin(o.angle) * o.depth}%`));
  const scale = useTransform(src, at((o) => 1 - 0.1 * o.depth * o.back));
  const rotate = useTransform(src, at((o) => 5 * Math.cos(o.angle) * o.depth));
  const zIndex = useTransform(src, at((o) => (Math.sin(o.angle) >= 0 ? 2 : 1)));
  const shade = useTransform(src, at((o) => 0.22 * o.depth * o.back));

  return (
    <motion.div
      data-testid={`approche-image-${index + 1}`}
      className="relative will-change-transform"
      style={{ x: main, y: cross, scale, rotate, zIndex }}
    >
      <div className={img.offset ? "relative md:translate-y-12" : "relative"}>
        <img src={img.src} alt="" className="w-full aspect-square object-cover rounded-2xl shadow-xl" />
        <motion.div className="absolute inset-0 rounded-2xl bg-[#1A1528] pointer-events-none" style={{ opacity: shade }} />
      </div>
    </motion.div>
  );
};

export const OrbitingPair = ({ images }) => {
  const ref = useRef(null);
  const busy = useRef(true);
  const inView = useInView(ref, { once: true, amount: 0.35 });
  const reduce = useReducedMotion();
  const progress = useMotionValue(0);
  const replay = useMotionValue(1);
  const fade = useTransform(progress, (p) => Math.min(1, p * (reduce ? 1 : 6)));

  useEffect(() => {
    if (!inView) return;
    const controls = animate(progress, 1, {
      ...(reduce ? { duration: 0.8 } : { duration: 2.4, ease: EASE }),
      onComplete: () => (busy.current = false),
    });
    return () => controls.stop();
  }, [inView, reduce, progress]);

  const onHover = () => {
    if (busy.current || reduce) return;
    busy.current = true;
    replay.set(0);
    animate(replay, 1, { duration: 2.4, ease: EASE, onComplete: () => (busy.current = false) });
  };

  return (
    <motion.div
      ref={ref}
      data-testid="approche-images"
      className="grid grid-cols-2 gap-3 md:gap-6 mt-16 mb-20"
      style={{ opacity: fade }}
      onHoverStart={onHover}
    >
      {images.map((img, i) => (
        <OrbitImage key={img.src} img={img} index={i} progress={progress} replay={replay} still={reduce} />
      ))}
    </motion.div>
  );
};
